import React, { useState, useEffect, useRef } from "react";
import { GET, RAW_POST } from "Common/api";
import { Button } from "Components/Base/Inputs/Button";
import { TextField } from "Components/Base/Inputs/TextField";
import { Grid } from "Components/Base/Layout/Grid";
import { Box } from "Components/Base/Layout/Box";
import { Typography } from "Components/Base/DataDisplay/Typography";
import { IconButton } from "Components/Base/Inputs/IconButton";
import Dropzone from "Components/Fragment/Dropzone";
import InputLabelDatePicker from "Components/Fragment/InputLabelDatePicker";
import PriorityDropdown from "lib/components/PriorityDropdown";
import Form, { Label } from "lib/components/Form";
import { Select } from "Components/Base/Inputs/Select";
import { MenuItem } from "Components/Base/Inputs/MenuItem";
import HoursAndMinutesTime from "Components/Fragment/HoursAndMinutesTime";
import { openExternalURL } from "lib/utils";
import PersonalGoalsDropdown from "lib/components/PersonalGoalsDropdown";
import SettingStore from "stores/settings";
import ValidationSummary from "lib/components/ValidationSummary";
import withLanguageContext from "lib/hocs/withLanguageContext";
import { OpenInNewIcon, DeleteOutlined } from "Components/Icons";
import SnackbarAlert from "Components/Fragment/SnackbarAlert";
import { TextFieldMultiline } from "Components/Base/Inputs/TextFieldMultiline";

const mobileViewport = window.matchMedia("(max-width: 400px)").matches || false;

const AddExternal = ({
  languageContext,
  activity = {},
  personalId,
  personalName,
  fromEducationHistory = false,
  allowAttachment,
  allowUsersToCompleteCourses,
  isManager,
  onCancel,
  save,
  error: propError,
}) => {
  // Refs for legacy components that have internal state
  const priorityDropdownRef = useRef(null);
  const personalGoalDropdownRef = useRef(null);

  // Consolidated form data state (1 state variable instead of 13)
  const [activityData, setActivityData] = useState({
    title: activity.title || "",
    description: activity.description || "",
    comment: activity.comments || "",
    responsible: activity.responsible || (activity.id ? "" : personalName),
    link: activity.link || "",
    linkLabel: activity.linkLabel || "",
    isCompleted: activity.isCompleted || false,
    startDate: activity.startDate || "",
    endDate: activity.endDate || "",
    expireDate: activity.dueDate || activity.deadlineDate || "",
    hours: parseInt(activity.hours, 10) || "",
    minutes: parseInt(activity.minutes, 10) || "",
    personalGoalId: activity.personalGoalId || activity.goalId || "",
    files: null,
    removeAttachment: false,
    id: activity.id || null,
    approvalRequested: activity.approvalRequested || false,
    certificateId: activity.certificateId || null,
  });

  const [uiState, setUiState] = useState({
    allowedExtensions: [],
    ready: false,
    errorMsgs: [],
    fileError: null,
    requireApprovalForCompletion: false,
    allowCertificateSelection: false,
    certificates: [],
    success: false,
    error: false,
    errorMessage: null,
  });

  // Helper to update form data
  const updateactivityData = (updates) => {
    setActivityData((prev) => ({ ...prev, ...updates }));
  };

  // Helper to update UI state
  const updateUiState = (updates) => {
    setUiState((prev) => ({ ...prev, ...updates }));
  };

  const validateActivity = (options = {}) => {
    const {
      requireStartEndForApproval = false,
      requireTitle = false,
      requireExpireDate = !fromEducationHistory,
      checkStartBeforeExpire = false,
      checkFileExtension = false,
    } = options;
    const errors = [];
    const deadlineDate =
      activityData.expireDate ||
      activity.dueDate ||
      activity.deadlineDate ||
      "";

    if (requireTitle && !activityData.title) {
      errors.push({ msg: languageContext("errEnterTitle") });
    }

    if (requireStartEndForApproval && !activityData.startDate) {
      errors.push({ msg: languageContext("enterStartdate") });
    }

    if (requireStartEndForApproval && !activityData.endDate) {
      errors.push({ msg: languageContext("enterEnddate") });
    }

    if (requireExpireDate && !deadlineDate) {
      errors.push({ msg: languageContext("enterExpiredate") });
    }

    if (
      activityData.startDate &&
      activityData.endDate &&
      activityData.startDate > activityData.endDate
    ) {
      errors.push({ msg: languageContext("badduration") });
    }

    if (
      checkStartBeforeExpire &&
      activityData.startDate &&
      activityData.expireDate &&
      activityData.startDate > activityData.expireDate
    ) {
      errors.push({ msg: languageContext("badexpiredate") });
    }

    if (
      checkFileExtension &&
      activityData.files &&
      !extOk(activityData.files[0].name)
    ) {
      errors.push({ msg: languageContext("extnotallowed") });
    }

    return errors;
  };

  const showValidationErrors = (errors) => {
    updateUiState({ errorMsgs: errors });
    const dialogContent = document.getElementById("dialog-content");
    if (dialogContent) {
      dialogContent.scrollTop = 0;
    }
  };

  const handleRequestApproval = async () => {
    const errors = validateActivity({
      requireTitle: true,
      requireStartEndForApproval: true,
      requireExpireDate: !fromEducationHistory,
      checkStartBeforeExpire: true,
      checkFileExtension: true,
    });

    if (errors.length > 0) {
      showValidationErrors(errors);
      return;
    }

    if (!window.confirm(languageContext("confirmrequestapproval"))) {
      return;
    }

    const deadlineDate =
      activityData.expireDate ||
      activity.dueDate ||
      activity.deadlineDate ||
      "";
    const data = buildSubmitData(deadlineDate);
    const approvalCallback = async (id) => {
      const response = await requestApproval(personalId, id);
      if (response.ok) {
        updateactivityData({ approvalRequested: true, id });
        updateUiState({ success: true });
      } else {
        updateUiState({ error: true });
      }
    };
    await save(data, activityData.files, null, approvalCallback);
  };

  const requestApproval = async (personalId, activityId) => {
    const response = await RAW_POST(
      `/v1/approval/${personalId}/personalplannedcourse/${activityId}`,
    );
    return response;
  };

  // Initialize component
  useEffect(() => {
    const initializeComponent = async () => {
      const extensions = await GET("/v1/allowedextensions");
      const availableCertificates = await GET(
        `/v1/users/${personalId}/accessible-certificates`,
      );
      const requireApprovalForCompletion =
        SettingStore.getSettingValue(
          "requireapprovalforexternalcoursecompletion",
        ) === "1";
      const allowCertificateSelection =
        SettingStore.getSettingValue(
          "AllowExternalCourseToGrantCertificate",
        ) === "1";
      updateUiState({
        allowedExtensions: extensions,
        ready: true,
        requireApprovalForCompletion,
        allowCertificateSelection,
        certificates: allowCertificateSelection ? availableCertificates : [],
      });
    };

    initializeComponent();
  }, []);

  // Handle prop error changes
  useEffect(() => {
    if (propError) {
      updateUiState({ fileError: languageContext(propError) });
    } else {
      updateUiState({ fileError: null });
    }
  }, [propError, languageContext]);

  const extOk = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    return uiState.allowedExtensions.includes(ext);
  };

  const handleDrop = (droppedFiles) => {
    updateactivityData({ files: droppedFiles, removeAttachment: false });
    updateUiState({ fileError: null });
  };

  const handleRemoveFile = () => {
    if (window.confirm(languageContext("confirmdeletefile"))) {
      updateactivityData({ removeAttachment: true, files: null });
    }
  };

  const updatePersonalGoalId = (personalGoalId) => {
    updateactivityData({ personalGoalId });
  };

  const handleSaveActivity = async (createApprovalRequest = false) => {
    const deadlineDate =
      activityData.expireDate ||
      activity.dueDate ||
      activity.deadlineDate ||
      "";
    const errors = validateActivity({
      requireTitle: true,
      requireStartEndForApproval: createApprovalRequest,
      requireExpireDate: !fromEducationHistory,
      checkStartBeforeExpire: true,
      checkFileExtension: true,
    });

    updateUiState({ errorMsgs: errors });

    if (errors.length === 0) {
      const data = buildSubmitData(deadlineDate);
      const createApprovalRequestCallback = createApprovalRequest
        ? (id) => requestApproval(personalId, id)
        : null;
      await save(data, activityData.files, null, createApprovalRequestCallback);
    } else {
      showValidationErrors(errors);
    }
  };

  const buildSubmitData = (deadlineDate) => {
    if (fromEducationHistory) {
      return {
        personalId,
        attachmentId: activity.attachmentId,
        title: activityData.title,
        comments: activityData.comment || "-",
        isCompleted: true,
        createdBy: personalId,
        description: activityData.description,
        startDate: activityData.startDate,
        endDate: activityData.endDate,
        isOtherTraining: false,
        removeAttachment: activityData.removeAttachment,
        hours: activityData.hours,
        minutes: activityData.minutes,
      };
    }

    return {
      personalId,
      attachmentId: activity.attachmentId,
      compTalkId: null,
      courseObjectId: null,
      title: activityData.title,
      comments: activityData.comment || "-",
      isCompleted: activityData.isCompleted,
      createdBy: personalId,
      period: "-",
      priority: priorityDropdownRef.current?.state?.currentValue || "-",
      description: activityData.description,
      showComment: false,
      startDate: activityData.startDate,
      endDate: activityData.endDate,
      isOtherTraining: false,
      courseId: null,
      deadlineDate,
      personalGoalId: activityData.personalGoalId,
      responsible: activityData.responsible,
      link: activityData.link,
      linkLabel: activityData.linkLabel,
      removeAttachment: activityData.removeAttachment,
      hours: activityData.hours,
      minutes: activityData.minutes,
      isExternalCourse: true,
      certificateId: activityData.certificateId,
    };
  };

  const getShowCompletedCheckbox = () => {
    if (fromEducationHistory) {
      return false;
    }

    if (isManager) {
      return true;
    }

    if (
      allowUsersToCompleteCourses === "1" &&
      !uiState.requireApprovalForCompletion
    ) {
      return true;
    }

    if (uiState.requireApprovalForCompletion && activity.approved) {
      return true;
    }

    return false;
  };

  const renderSelectedFiles = () => {
    if (activityData.files) {
      return activityData.files.map((f) => (
        <Box key={f.name} className="dropzone-selectedfiles-file">
          {f.name}
        </Box>
      ));
    }

    if (activity.attachmentId && !activityData.removeAttachment) {
      return (
        <Box key={activity.fileName} className="dropzone-selectedfiles-file">
          {activity.fileName}
        </Box>
      );
    }

    return null;
  };

  if (!uiState.ready) {
    return <Box />;
  }

  const formContentStyle = mobileViewport
    ? {}
    : { maxHeight: "650px", overflow: "auto" };
  const disableForm =
    (activityData.approvalRequested && !isManager) ||
    (fromEducationHistory && uiState.requireApprovalForCompletion) ||
    (activity.approved && activityData.isCompleted);
  const saveButtonDisabled =
    disableForm && !(activity.approved && activityData.isCompleted);
  const showCompletedCheckbox = getShowCompletedCheckbox();

  return (
    <Form onSubmit={handleSaveActivity}>
      <Box
        className={`${!mobileViewport ? "scrollbar " : ""}add-external-inner-container`}
        sx={formContentStyle}
      >
        {uiState.errorMsgs.length > 0 && (
          <ValidationSummary errors={uiState.errorMsgs} />
        )}

        <Box mb={1.25}>
          <Label
            htmlFor="addactivity-title"
            className="required"
            label={`${languageContext("title")}:`}
          />
          <TextField
            id="addactivity-title"
            fullWidth
            disabled={disableForm}
            value={activityData.title}
            onChange={(e) => updateactivityData({ title: e.target.value })}
            required
          />
        </Box>

        <Box mb={1.25}>
          <Typography
            component="label"
            htmlFor="TxtDescription"
            variant="body1"
          >
            {languageContext("description")}:
          </Typography>
          <TextFieldMultiline
            id="TxtDescription"
            minRows={3}
            fullWidth
            disabled={disableForm}
            value={activityData.description}
            onChange={(e) =>
              updateactivityData({ description: e.target.value })
            }
          />
        </Box>

        {!fromEducationHistory && (
          <Box mb={1.25}>
            <Typography component="label" htmlFor="SelPrio" variant="body1">
              {languageContext("priority")}:
            </Typography>
            <PriorityDropdown
              ref={priorityDropdownRef}
              defaultValue={activity.priority}
              id="SelPrio"
              disabled={disableForm}
            />
          </Box>
        )}
        {(uiState.allowCertificateSelection || fromEducationHistory) && (
          <Box mb={1.25} sx={{ width: "50%" }}>
            <Typography
              component="label"
              htmlFor="SelCertificate"
              variant="body1"
            >
              {languageContext("certificate")}:
            </Typography>
            {fromEducationHistory ? (
              <TextField
                id="SelCertificate"
                fullWidth
                disabled
                value={
                  activity.certificates?.[0]?.certificate?.title ??
                  activity.Certificates?.Certificate?.[0]?.Title ??
                  ""
                }
                inputProps={{
                  "aria-label": languageContext("certificate"),
                }}
              />
            ) : (
              <Select
                id="SelCertificate"
                fullWidth
                value={activityData.certificateId || ""}
                onChange={(e) =>
                  updateactivityData({ certificateId: e.target.value })
                }
                label={languageContext("selectcertificate")}
                displayEmpty
                inputProps={{
                  "aria-label": languageContext("selectcertificate"),
                  "aria-required": "false",
                }}
                disabled={disableForm}
              >
                <MenuItem value="">
                  - {languageContext("choosecertificate")} -
                </MenuItem>
                {uiState.certificates &&
                  uiState.certificates.map(({ id, title }) => (
                    <MenuItem key={id} value={id}>
                      {title}
                    </MenuItem>
                  ))}
              </Select>
            )}
          </Box>
        )}

        <Box display="flex" sx={{ gap: 2, flexWrap: "wrap", mb: 1.25 }}>
          {!fromEducationHistory && (
            <InputLabelDatePicker
              id="CalExpireDate"
              required
              label={languageContext("expiredate")}
              name={languageContext("expiredate")}
              value={activityData.expireDate}
              disabled={disableForm}
              onChange={(event) =>
                updateactivityData({ expireDate: event.target.value })
              }
              oldStyleLabel={true}
            />
          )}

          <InputLabelDatePicker
            id="CalStartDate"
            disabled={disableForm}
            label={languageContext("startdate")}
            name={languageContext("startdate")}
            value={activityData.startDate}
            onChange={(event) =>
              updateactivityData({ startDate: event.target.value })
            }
            oldStyleLabel={true}
          />

          <InputLabelDatePicker
            id="CalEndDate"
            label={languageContext("enddate")}
            name={languageContext("enddate")}
            value={activityData.endDate}
            disabled={disableForm}
            onChange={(event) =>
              updateactivityData({ endDate: event.target.value })
            }
            oldStyleLabel={true}
          />
        </Box>

        <Box mb={1.25}>
          <HoursAndMinutesTime
            itemHours={activityData.hours}
            disabled={disableForm}
            setItemHours={(hours) => updateactivityData({ hours })}
            itemMinutes={activityData.minutes}
            setItemMinutes={(minutes) => updateactivityData({ minutes })}
            hourLabel={languageContext("hours")}
            minutesLabel={languageContext("minutes")}
          />
        </Box>

        {!fromEducationHistory && (
          <Box mb={1.25}>
            <Typography
              component="label"
              htmlFor="SelPersonalGoal"
              variant="body1"
            >
              {languageContext("connecttogoal")}:
            </Typography>
            <PersonalGoalsDropdown
              ref={personalGoalDropdownRef}
              id="SelPersonalGoal"
              personalId={personalId}
              defaultValue={activity.personalGoalId || activity.goalId}
              updateParentState={updatePersonalGoalId}
              disabled={disableForm}
            />
          </Box>
        )}

        {!fromEducationHistory && (
          <Box mb={1.25}>
            <Typography
              component="label"
              htmlFor="TxtResponsible"
              variant="body1"
            >
              {languageContext("responsible1")}:
            </Typography>
            <TextField
              id="TxtResponsible"
              fullWidth
              disabled={disableForm}
              value={activityData.responsible}
              onChange={(e) =>
                updateactivityData({ responsible: e.target.value })
              }
            />
          </Box>
        )}

        {!fromEducationHistory && (
          <Box display="flex" flexWrap="wrap" mb={1.25} gap={2}>
            <Box flex={1} minWidth="200px">
              <Typography
                component="label"
                htmlFor="addactivity-link"
                variant="body1"
              >
                {languageContext("html-editor-link")}:
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  id="addactivity-link"
                  disabled={disableForm}
                  value={activityData.link}
                  onChange={(e) => updateactivityData({ link: e.target.value })}
                  fullWidth
                />
                <IconButton
                  onClick={() =>
                    activityData.link && openExternalURL(activityData.link)
                  }
                  icon={<OpenInNewIcon />}
                  title={languageContext("openlink")}
                  disabled={!activityData.link || disableForm}
                />
              </Box>
            </Box>
            <Box flex={1} minWidth="200px">
              <Typography
                component="label"
                htmlFor="addactivity-link-label"
                variant="body1"
              >
                {languageContext("linklabel")}:
              </Typography>
              <TextField
                id="addactivity-link-label"
                disabled={disableForm}
                value={activityData.linkLabel}
                onChange={(e) =>
                  updateactivityData({ linkLabel: e.target.value })
                }
                fullWidth
              />
            </Box>
          </Box>
        )}

        {allowAttachment && (
          <Box mb={1.25}>
            <Typography
              component="label"
              htmlFor="dropzoneInput"
              variant="body1"
            >
              {`${languageContext("document")} (${languageContext(
                "maxfilesize",
              ).toLowerCase()}: ${SettingStore.getSettingValue(
                "activityAttachmentMaxSize",
              )} MB):`}
            </Typography>

            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="body2">
                {languageContext("selectedfile")}:
              </Typography>
              {(activityData.files || activity.attachmentId) && (
                <>
                  <Box className="dropzone-selectedfiles">
                    {renderSelectedFiles()}
                  </Box>
                  {!activityData.removeAttachment && (
                    <IconButton
                      onClick={handleRemoveFile}
                      icon={<DeleteOutlined />}
                      title={languageContext("delete")}
                      size="small"
                    />
                  )}
                </>
              )}
            </Box>

            {uiState.fileError && (
              <Typography variant="body2" color="error" mb={1}>
                {uiState.fileError.replace(
                  "%maxfilesize%",
                  `${SettingStore.getSettingValue("activityAttachmentMaxSize")} MB`,
                )}
              </Typography>
            )}

            <Dropzone
              onDrop={handleDrop}
              label={
                activityData.files || activity.attachmentId
                  ? languageContext("dropzoneinfo2")
                  : languageContext("dropzoneinfo")
              }
              allowedExtensions={uiState.allowedExtensions}
              disabled={disableForm}
            />
          </Box>
        )}

        <Box mb={1.25}>
          <Typography component="label" htmlFor="TxtComment" variant="body1">
            {languageContext("comment")}:
          </Typography>
          <TextFieldMultiline
            id="TxtComment"
            minRows={3}
            fullWidth
            disabled={disableForm}
            value={activityData.comment}
            onChange={(e) => updateactivityData({ comment: e.target.value })}
          />
        </Box>

        {showCompletedCheckbox && (
          <Box mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <input
                type="checkbox"
                id="ChkCompleted"
                checked={activityData.isCompleted}
                disabled={activity.approved && activityData.isCompleted}
                onChange={(e) =>
                  updateactivityData({ isCompleted: e.target.checked })
                }
              />
              <Typography
                component="label"
                htmlFor="ChkCompleted"
                variant="body1"
                sx={{ cursor: "pointer" }}
              >
                {languageContext("coursecompleted")}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Grid container spacing={1} justifyContent="flex-end">
        <Grid item>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onCancel}
            label={languageContext("cancel")}
          />
        </Grid>
        <Grid item>
          <Button
            type="submit"
            label={languageContext("save")}
            disabled={saveButtonDisabled}
          />
        </Grid>
        {!fromEducationHistory &&
          uiState.requireApprovalForCompletion &&
          !activity.approved &&
          activity.id &&
          !activityData.approvalRequested &&
          !isManager && (
            <Grid item>
              <Button
                color="secondary"
                variant="outlined"
                type="button"
                label={languageContext("requestapproval")}
                onClick={handleRequestApproval}
              />
            </Grid>
          )}
        {!fromEducationHistory &&
          uiState.requireApprovalForCompletion &&
          !activity.approved &&
          !activity.id &&
          !isManager && (
            <Grid item>
              <Button
                color="secondary"
                variant="outlined"
                type="button"
                label={languageContext("saveandrequestapproval")}
                onClick={() => handleSaveActivity(true)}
              />
            </Grid>
          )}
      </Grid>
      <SnackbarAlert
        open={uiState.success}
        onClose={() => updateUiState({ success: false })}
        message={languageContext("approvalrequested")}
        severity="success"
      />
      <SnackbarAlert
        open={uiState.error}
        onClose={() => updateUiState({ error: false })}
        message={languageContext("error")}
        severity="error"
      />
    </Form>
  );
};

export default withLanguageContext(AddExternal, "TalentManagement");
